/**
 * Form state for multi-country bulk edit modal.
 * Manages shared fields, per-country overrides, interval mode, and step nav.
 */

import { useCallback, useMemo, useState } from 'react';

import { parseIntervalHHMM } from '@/features/smswiser-campaigns/utils/scheduleInterval';
import { useShortlinkOptionsState } from '@/features/smswiser-campaigns/hooks/useShortlinkOptionsState';

import type {
  IntervalMode,
  MultiCountryBulkEditConfig,
  PerCountryOverride,
  TriState,
} from '../shared';
import { useMultiCountryCreativeState } from './useMultiCountryCreativeState';

export type WizardStep = 1 | 2 | 3;
export type MultiCountryScheduleMode = 'unchanged' | 'apply' | 'clear';

export interface UseMultiCountryBulkEditFormParams {
  selectedCountryKeys: string[];
}

export function useMultiCountryBulkEditForm(params: UseMultiCountryBulkEditFormParams) {
  const { selectedCountryKeys } = params;
  const creative = useMultiCountryCreativeState();

  const [step, setStep] = useState<WizardStep>(1);
  const goNext = useCallback(() => setStep((s) => Math.min(s + 1, 3) as WizardStep), []);
  const goBack = useCallback(() => setStep((s) => Math.max(s - 1, 1) as WizardStep), []);

  const [amount, setAmount] = useState<number | ''>('');
  const [tldValue, setTldValue] = useState('');
  const [executeAfter, setExecuteAfter] = useState('');
  const [scheduleMode, setScheduleModeState] = useState<MultiCountryScheduleMode>('unchanged');
  const [scheduleInterval, setScheduleInterval] = useState('');
  const [intervalMode, setIntervalMode] = useState<IntervalMode>('per_country');
  const [staticTagId, setStaticTagId] = useState<TriState<string | null>>('unchanged');
  const [forceSend, setForceSend] = useState<TriState<boolean>>('unchanged');
  const [batchAmount, setBatchAmount] = useState<number | null>(null);
  const [batchDelay, setBatchDelay] = useState<number | null>(null);
  const [useOnlyNewDomains, setUseOnlyNewDomains] = useState<TriState<boolean>>('unchanged');
  const shortlink = useShortlinkOptionsState();
  const [aiOptEnabled, setAiOptEnabled] = useState(false);
  const [aiOptType, setAiOptType] = useState<'ROI' | 'CTR' | null>(null);
  const [aiOptBatch, setAiOptBatch] = useState<number | null>(null);
  const [aiOptMinPercent, setAiOptMinPercent] = useState<number | null>(null);
  const [aiOptSendUntil, setAiOptSendUntil] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, PerCountryOverride>>({});
  const [propagateToCountries, setPropagateToCountries] = useState<string[]>([]);

  const setScheduleMode = useCallback((m: MultiCountryScheduleMode) => {
    setScheduleModeState(m);
    if (m === 'unchanged' || m === 'clear') {
      setExecuteAfter('');
      setScheduleInterval('');
    }
  }, []);

  const handleSetExecuteAfter = useCallback((v: string) => {
    setExecuteAfter(v);
    if (v.trim()) setScheduleModeState('apply');
  }, []);

  const setCountryOverride = useCallback((ck: string, patch: Partial<PerCountryOverride>) => {
    setOverrides((prev) => ({ ...prev, [ck]: { ...prev[ck], ...patch } }));
  }, []);

  const scheduleIntervalMinutes = parseIntervalHHMM(scheduleInterval);

  const allCountryKeys = useMemo(() => {
    const combined = new Set([...selectedCountryKeys, ...propagateToCountries]);
    return [...combined].sort();
  }, [selectedCountryKeys, propagateToCountries]);

  const buildConfig = useCallback((): MultiCountryBulkEditConfig => {
    const cfg: MultiCountryBulkEditConfig = {
      intervalMode, scheduleInterval, staticTagId, forceSend, useOnlyNewDomains,
      clickIdStructure: shortlink.clickIdStructure, domainScheme: shortlink.domainScheme,
      overridesByCountry: overrides, propagateToCountries,
      ...creative.buildCreativePayload(),
    };
    if (amount !== '' && amount >= 1) cfg.amount = amount;
    if (tldValue.trim()) cfg.shortLinkTldValue = tldValue.trim();
    if (scheduleMode === 'clear') {
      cfg.executeAfter = null;
    } else if (scheduleMode === 'apply' && executeAfter.trim()) {
      cfg.executeAfter = executeAfter.includes('Z') || executeAfter.includes('+')
        ? executeAfter.trim() : new Date(executeAfter).toISOString();
    }
    if (batchAmount != null) cfg.batchAmount = batchAmount;
    if (batchDelay != null) cfg.batchDelay = batchDelay;
    if (aiOptEnabled) {
      cfg.aiOptimizationEnabled = true;
      if (aiOptType) cfg.aiOptimizationType = aiOptType;
      if (aiOptBatch != null) cfg.aiOptimizationBatchAmount = aiOptBatch;
      if (aiOptMinPercent != null) cfg.aiOptimizationMinimalValuePercent = aiOptMinPercent;
      if (aiOptSendUntil) cfg.aiOptimizationSendUntil = aiOptSendUntil;
    }
    return cfg;
  }, [
    amount, tldValue, executeAfter, scheduleMode, scheduleInterval, intervalMode,
    staticTagId, forceSend, batchAmount, batchDelay, useOnlyNewDomains,
    shortlink.clickIdStructure, shortlink.domainScheme,
    aiOptEnabled, aiOptType, aiOptBatch, aiOptMinPercent, aiOptSendUntil,
    overrides, propagateToCountries, creative,
  ]);

  const hasPerCountryCreative = Object.values(creative.perCountryTexts).some((v) => v.length > 0) ||
    Object.values(creative.perCountrySenders).some((v) => v.length > 0);

  const hasAnyChange = useMemo(() => (
    (amount !== '' && amount >= 1) || tldValue.trim() !== '' ||
    scheduleMode === 'clear' || (scheduleMode === 'apply' && executeAfter.trim() !== '') ||
    staticTagId !== 'unchanged' ||
    (forceSend !== 'unchanged' && forceSend !== 'mixed') ||
    (useOnlyNewDomains !== 'unchanged' && useOnlyNewDomains !== 'mixed') ||
    shortlink.picked ||
    aiOptEnabled || batchAmount != null || batchDelay != null ||
    creative.selectedSenderNames.length > 0 || creative.selectedSmsTexts.length > 0 ||
    creative.perDropSmsTexts != null || creative.perDropSenderNames != null ||
    hasPerCountryCreative || Object.keys(overrides).length > 0 ||
    propagateToCountries.length > 0
  ), [
    amount, tldValue, executeAfter, scheduleMode,
    staticTagId, forceSend, useOnlyNewDomains, aiOptEnabled, batchAmount, batchDelay,
    shortlink.picked, creative, overrides, hasPerCountryCreative, propagateToCountries.length,
  ]);

  return {
    step, setStep, goNext, goBack,
    selectedCountryKeys, allCountryKeys,
    amount, setAmount, tldValue, setTldValue,
    executeAfter, setExecuteAfter: handleSetExecuteAfter,
    scheduleMode, setScheduleMode,
    scheduleInterval, setScheduleInterval, scheduleIntervalMinutes,
    intervalMode, setIntervalMode,
    staticTagId, setStaticTagId,
    forceSend, setForceSend,
    batchAmount, setBatchAmount, batchDelay, setBatchDelay,
    useOnlyNewDomains, setUseOnlyNewDomains,
    shortlink,
    aiOptEnabled, setAiOptEnabled,
    aiOptType, setAiOptType,
    aiOptBatch, setAiOptBatch,
    aiOptMinPercent, setAiOptMinPercent,
    aiOptSendUntil, setAiOptSendUntil,
    overrides, setCountryOverride,
    propagateToCountries, setPropagateToCountries,
    ...creative,
    buildConfig, hasAnyChange,
  };
}
